import type { ACF } from 'types/acf';
import type { BlockEvents } from 'types/block-event';
import React from 'react';
import Image from 'next/image';
import { Paragraph, Wrap } from '@leafwell/components';
import VpdSignupForm from './vpd-signup-form';
import Figure from './figure';
import {
  calculateVpdDiscount,
  calculateVpdNewPrice,
} from 'lib/utils/calculateVpdDiscount';
import { useWordPressContext } from 'contexts/wordpress-provider';
import HeadingBody from './heading-body';

const HeroDiscountLabel: React.FC<{
  newPrice?: number;
  discount?: number;
  isPercentage?: boolean;
}> = ({ newPrice, discount, isPercentage }) => {
  return newPrice > 0 ? (
    <span className="self-center lg:self-start flex gap-x-2 items-center">
      ${newPrice}
      <span className="inline-flex items-center h-fit rounded-3xl select-none px-4 py-2 bg-lilac-300 text-black align-center text-base lg:text-2xl font-medium">
        SAVE {isPercentage ? '' : '$'}
        {discount}
        {isPercentage ? '%' : ''}
      </span>
    </span>
  ) : (
    <span className="self-center lg:self-start flex gap-x-2 items-center">
      <span className="inline-flex items-center h-fit rounded-3xl select-none px-4 py-2 bg-lilac-300 text-black align-center text-base lg:text-2xl font-medium">
        SAVE {isPercentage ? '' : '$'}
        {discount}
        {isPercentage ? '%' : ''}
      </span>
    </span>
  );
};

const HeroBadge: React.FC<{
  icon?: string;
  label?: string;
}> = ({ icon, label }) => (
  <article className="flex flex-col gap-y-4 xl:flex-row xl:gap-x-2 xl:items-center">
    <span className="flex h-14 xl:h-auto justify-center items-end">
      <Image
        src={icon}
        width={48}
        height={48}
        alt="values header icon"
        quality={50}
      />
    </span>
    <Paragraph className="max-w-24 lg:max-w-28 text-xs xl:text-sm font-medium text-center">
      {label}
    </Paragraph>
  </article>
);

const HeroVpd: React.FC<{
  hero: Pick<ACF['hero'], 'title' | 'description' | 'formTitle'>;
  states: ACF['states'];
  vpdLogo: ACF['vpdLogo'];
  vpdPartnerName: ACF['vpdPartnerName'];
  price: ACF['price'];
  events?: BlockEvents['events'];
  patientRenewalDropdown: boolean;
  patientRenewalDropdownInfo: string;
}> = ({
  hero: { title, description, formTitle },
  states,
  vpdPartnerName,
  vpdLogo,
  price: {
    newPrice,
    price,
    percentageDiscount,
    discountCode,
    freeDiscountCode,
  },
  events,
  patientRenewalDropdown,
  patientRenewalDropdownInfo,
}) => {
  const isMultiState = states?.length == 0 || states?.length > 1;
  const { translations } = useWordPressContext();
  const discount = calculateVpdDiscount(price, newPrice);
  const calculatedNewPrice = calculateVpdNewPrice(price, percentageDiscount);

  const badges = [
    {
      label: translations['Best Care, Best Value'],
      icon: '/diamond-solid.svg',
    },
    {
      label: translations['Quick and Easy Process'],
      icon: '/clock-solid.svg',
    },
    {
      label: translations['Get Approved or Your Money Back'],
      icon: '/hand-dollar-solid.svg',
    },
  ];

  return (
    <section className="relative grid bg-lilac-50 xl:-mt-10 py-10 xl:py-20">
      <Wrap
        {...{
          className: 'grid lg:grid-cols-2 gap-y-32 items-start gap-x-10 ',
          spacer: false,
        }}
      >
        <div className="relative flex flex-col gap-y-6 lg:gap-y-10 items-center lg:items-start text-pretty">
          {vpdLogo && (
            <Figure
              {...vpdLogo}
              width={120}
              height={120}
              className="lg:w-fit"
            />
          )}
          <div className="text-lg text-center lg:text-left">
            {!freeDiscountCode ? (
              <h1
                className={[
                  'flex flex-col',
                  price > 0 ? '' : 'gap-y-4',
                  'mb-6 lg:mb-8 text-4xl lg:text-5xl xl:text-6xl font-sans prose font-semibold',
                ].join(' ')}
              >
                {title}
                <HeroDiscountLabel
                  newPrice={newPrice > 0 ? newPrice : calculatedNewPrice}
                  discount={discount > 0 ? discount : percentageDiscount}
                  isPercentage={percentageDiscount > 0 ? true : false}
                />
              </h1>
            ) : (
              <HeadingBody
                title={title}
                level="1"
                tagName="h1"
                className="!mb-6 lg:!mb-8 font-semibold"
              />
            )}
            {description?.length > 0 ? (
              <div dangerouslySetInnerHTML={{ __html: description }}></div>
            ) : null}
          </div>
          {badges?.length > 0 ? (
            <div className="flex gap-x-6">
              {badges.map(({ label, icon }, idx) => (
                <HeroBadge key={`badge-${idx}`} label={label} icon={icon} />
              ))}
            </div>
          ) : null}
        </div>
        <div className="w-full max-w-md justify-self-center items-center">
          <VpdSignupForm
            formTitle={formTitle}
            vpdPartnerName={vpdPartnerName ? (vpdPartnerName as string) : null}
            price={price}
            newPrice={newPrice > 0 ? newPrice : calculatedNewPrice}
            discount={discount > 0 ? discount : percentageDiscount}
            isDiscountPercentage={percentageDiscount > 0 ? true : false}
            discountCode={discountCode}
            freeDiscountCode={freeDiscountCode}
            state={states}
            events={events}
            isMultiState={isMultiState}
            patientRenewalDropdown={patientRenewalDropdown}
            patientRenewalDropdownInfo={patientRenewalDropdownInfo}
          />
        </div>
      </Wrap>
    </section>
  );
};
export default HeroVpd;
